import { fetchAPI } from "./utils";

export interface PromptVoice {
    id: string;
    name: string;
    description?: string;
    externalVoiceId?: string;
    gender?: string;
    accent?: string;
    age?: string;
    languageAIId?: string;
    origin?: string;
    status: string;
    demoUrl?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PromptVoiceStats {
    used: number;
    limit: number;
    remaining: number;
    canCreateMore: boolean;
}

export interface PromptVoicePreview {
    id: string;
    externalVoiceId: string;
    audioBase64: string | null; // Base64 encoded audio, played directly from browser memory
    duration: number | null;
    language: string | null;
}

export interface DesignPromptVoiceParams {
    token: string;
    voiceDescription: string;
    previewText?: string;
    autoGenerateText?: boolean;
    loudness?: number;
    guidanceScale?: number;
    gender?: string;
    accent?: string;
    age?: string;
    language?: string;
}

export interface DesignPromptVoiceResponse {
    sessionId: string;
    previews: PromptVoicePreview[];
    text: string;
}

export interface SavePromptVoiceParams {
    token: string;
    externalVoiceId: string;
    name: string;
    description?: string;
    gender?: string;
    accent?: string;
    age?: string;
    language?: string;
}

export interface SavePromptVoiceResponse {
    message: string;
    promptVoice: PromptVoice;
}

/**
 * Get prompt voice statistics (used, remaining, etc.)
 */
export const getPromptVoiceStats = async (token: string): Promise<PromptVoiceStats> => {
    return fetchAPI<PromptVoiceStats>("/prompt-voice-stats", "GET", undefined, token);
};

/**
 * Design/generate voice previews from a text description
 */
export const designPromptVoice = async ({
    token,
    voiceDescription,
    previewText,
    autoGenerateText = true,
    loudness = 0.5,
    guidanceScale = 5,
    gender,
    accent,
    age,
    language,
}: DesignPromptVoiceParams): Promise<DesignPromptVoiceResponse> => {
    return fetchAPI<DesignPromptVoiceResponse>(
        "/design-prompt-voice",
        "POST",
        {
            voiceDescription,
            previewText,
            autoGenerateText,
            loudness,
            guidanceScale,
            gender,
            accent,
            age,
            language,
        },
        token,
    );
};

/**
 * Save a selected preview voice as permanent
 */
export const savePromptVoice = async ({
    token,
    externalVoiceId,
    name,
    description,
    gender,
    accent,
    age,
    language,
}: SavePromptVoiceParams): Promise<SavePromptVoiceResponse> => {
    return fetchAPI<SavePromptVoiceResponse>(
        `/save-prompt-voice/${externalVoiceId}`,
        "POST",
        {
            name,
            description,
            gender,
            accent,
            age,
            language,
        },
        token,
    );
};

/**
 * Get all prompt voices for the current user
 */
export const getPromptVoices = async (token: string): Promise<PromptVoice[]> => {
    return fetchAPI<PromptVoice[]>("/get-prompt-voices", "GET", undefined, token);
};
